import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { NIL as EMPTY_GUID } from 'uuid';
import { BaseHttpService } from '@nimbus-library/features/shared/services/base-http/base-http.service';
import {
  CiUnifiedSessionRequest,
  CiUnifiedSessionResponse,
} from '@nimbus-portal/features/customer-insights/model/ci-unified-session.model';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { NimbusPermission } from '@nimbus-global/features/permissions/models/enums/nimbus-permission.enum';
import { CiServiceSessionModel } from '@nimbus-portal/features/customer-insights/model/ci-service-session.model';
import { ICustomerSummaryQuery, ICustomerSummaryResponse } from '@nimbus-portal/features/customer-insights/model/customer-summary.model';

@Injectable({ providedIn: 'root' })
export class CiUnifiedSessionsService {
  public constructor(
    private readonly baseHttpService: BaseHttpService,
    private readonly authenticationService: IAuthenticationService,
    private readonly permissionManagerService: IPermissionManagerService,
  ) {}

  public loadUnifiedSessions(tenantId: string, unifiedSessionRequest: CiUnifiedSessionRequest): Observable<CiUnifiedSessionResponse> {
    return this.baseHttpService.post(`reporting/customer-overview/tenants/${tenantId}/sessions`, unifiedSessionRequest);
  }

  public isCustomerOverviewEnabled(): Observable<boolean> {
    return combineLatest([
      this.authenticationService.isAuthenticated$.pipe(filter(Boolean)),
      this.permissionManagerService.getPermissionsModel$().pipe(filter(Boolean)),
    ]).pipe(
      take(1),
      switchMap(([, permissionModel]) => {
        if (!permissionModel.hasPermission(NimbusPermission.CustomerOverviewRead)) {
          return of(false);
        }
        const tenantId = this.authenticationService.getUserInfo()?.tenantId || EMPTY_GUID;
        return this.baseHttpService.get(`organization-unit-manager/v2/tenants/${tenantId}/user-customer-overview-visibility`);
      }),
    );
  }

  public loadUnifiedSessionDetails(tenantId: string, sessionId: string): Observable<CiServiceSessionModel[]> {
    return this.baseHttpService.get(`reporting/customer-overview/tenants/${tenantId}/sessions/${sessionId}`);
  }

  public getCustomerSummary(tenantId: string, query: ICustomerSummaryQuery): Observable<ICustomerSummaryResponse> {
    const queryParts: string[] = [];
    if (query.phone) {
      queryParts.push(`phone=${encodeURIComponent(query.phone)}`);
    }
    if (query.email) {
      queryParts.push(`email=${encodeURIComponent(query.email)}`);
    }
    const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';
    return this.baseHttpService.get(`reporting/customer-overview/tenants/${encodeURIComponent(tenantId)}/customer-summary${queryString}`);
  }
}
